<script setup>
import { ref, watch, computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import MultiSelect from 'primevue/multiselect';
import { BUSINESS_MODELS, permissionsArrayToMatrix, permissionsMatrixToArray } from '@/constants/permissions.js';

const props = defineProps({
  permissions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:permissions']);

// Внутреннее состояние матрицы
const matrix = ref({});

// Инициализация матрицы из массива permissions
watch(() => props.permissions, (newPerms) => {
  matrix.value = permissionsArrayToMatrix(newPerms);
}, { immediate: true });

// Данные для таблицы: каждая модель - строка
const tableData = computed(() => {
  return BUSINESS_MODELS.map(model => {
    const modelPerms = matrix.value[model.id] || {
      view: false,
      add: false,
      change: false,
      delete: false,
      special: []
    };

    return {
      id: model.id,
      label: model.label,
      view: modelPerms.view,
      add: modelPerms.add,
      change: modelPerms.change,
      delete: modelPerms.delete,
      special: modelPerms.special,
      specialOptions: model.permissions.special
    };
  });
});

function togglePermission(modelId, permType) {
  if (!matrix.value[modelId]) {
    matrix.value[modelId] = { view: false, add: false, change: false, delete: false, special: [] };
  }
  matrix.value[modelId][permType] = !matrix.value[modelId][permType];
  emitUpdate();
}

function updateSpecialPermissions(modelId, selectedSpecial) {
  if (!matrix.value[modelId]) {
    matrix.value[modelId] = { view: false, add: false, change: false, delete: false, special: [] };
  }
  matrix.value[modelId].special = selectedSpecial || [];
  emitUpdate();
}

function emitUpdate() {
  const permissionsArray = permissionsMatrixToArray(matrix.value);
  emit('update:permissions', permissionsArray);
}
</script>

<template>
  <div class="permissions-matrix">
    <DataTable
      :value="tableData"
      scrollable
      scroll-height="500px"
      class="text-sm"
    >
      <Column field="label" header="Модель" frozen style="min-width: 150px; font-weight: 600;"></Column>

      <Column header="Просмотр" style="width: 100px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.view"
            :binary="true"
            @update:model-value="togglePermission(data.id, 'view')"
          />
        </template>
      </Column>

      <Column header="Добавление" style="width: 110px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.add"
            :binary="true"
            @update:model-value="togglePermission(data.id, 'add')"
          />
        </template>
      </Column>

      <Column header="Изменение" style="width: 110px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.change"
            :binary="true"
            @update:model-value="togglePermission(data.id, 'change')"
          />
        </template>
      </Column>

      <Column header="Удаление" style="width: 100px; text-align: center;">
        <template #body="{ data }">
          <Checkbox
            :model-value="data.delete"
            :binary="true"
            @update:model-value="togglePermission(data.id, 'delete')"
          />
        </template>
      </Column>

      <Column header="Специальные действия" style="min-width: 300px;">
        <template #body="{ data }">
          <MultiSelect
            v-if="data.specialOptions.length > 0"
            :model-value="data.special"
            :options="data.specialOptions"
            option-label="label"
            option-value="code"
            placeholder="Выберите действия"
            class="w-full"
            display="chip"
            :show-toggle-all="false"
            @update:model-value="updateSpecialPermissions(data.id, $event)"
          />
          <span v-else class="text-gray-400">—</span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
.permissions-matrix :deep(.p-datatable-thead > tr > th) {
  font-size: 0.875rem;
  padding: 0.5rem;
}

.permissions-matrix :deep(.p-datatable-tbody > tr > td) {
  padding: 0.5rem;
}

.permissions-matrix :deep(.p-checkbox) {
  width: 18px;
  height: 18px;
}
</style>
